<template>
  <div>
    <ConfirmPopup
      v-if="showConfirmPopup"
      :popupContext="currentPopupContext"
      @popupEvent="popupEventHandler"
    ></ConfirmPopup>

    <div
      id="journal"
      class="teacher-journal"
      :class="{ 'teacher-journal--student': !isTeacher }"
      v-if="!journalNew.isEmpty()"
    >
      <v-tooltip
        bottom
        :content-class="tooltipClass"
        v-model="filterBtnTTip"
        max-width="250"
        :position-x="tooltipX"
        :position-y="tooltipY"
      >
        {{ tooltipText }}
      </v-tooltip>
      <v-tooltip
        bottom
        :content-class="infoTooltipClass"
        v-model="infoTooltip"
        max-width="2000"
        :position-x="infoTooltipX"
        :position-y="infoTooltipY"
      >
        {{ infoTooltipText }}
      </v-tooltip>
      <div v-if="isTeacher" class="journal-controls">
        <div class="journal-controls__primary">
          <div class="journal-controls__filters">
            <v-text-field
              v-model="searchByName"
              class="journal-controls__search"
              prepend-inner-icon="mdi-magnify"
              label="Знайти учня"
              clearable
              dense
              hide-details
              outlined
            ></v-text-field>
            <v-combobox
              v-model="selectFilter"
              class="journal-controls__filter"
              :disabled="operationInProgress"
              :items="filterItems"
              label="Типи оцінок"
              clearable
              dense
              hide-details
              multiple
              outlined
              small-chips
            ></v-combobox>
            <div class="journal-controls__color">
              <v-switch
                v-model="allowColorTheme"
                class="ma-0 pa-0"
                aria-label="Кольорові оцінки"
                hide-details
              ></v-switch>
              <button type="button" @click="allowColorTheme = !allowColorTheme">
                Кольорові оцінки
              </button>
            </div>
          </div>

          <div class="journal-controls__actions">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="journal-controls__add"
                  outlined
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="addCol"
                >
                  Додати стовпчик
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="(item, index) in addItems"
                  :key="index"
                  @click="selectType(item)"
                >
                  <v-list-item-title>{{ item.label }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>

        <JournalColumnEditor
          v-if="selectedType"
          :items="addItems"
          :selected-type="selectedType"
          :instruction="addColumnInstruction"
          :selected-range-description="selectedRangeDescription"
          :has-selected-range="hasSelectedRange"
          :operation-in-progress="operationInProgress"
          @select-type="selectType"
          @cancel="cancelAddColumn"
          @save="addColumnServer"
        />

        <div class="journal-lessons-navigation">
          <div class="journal-lessons-navigation__summary">
            <span class="journal-lessons-navigation__label">Уроки</span>
            <strong>{{ rangeLabel }}</strong>
          </div>
          <div class="journal-lessons-navigation__controls">
            <span>Стовпців:</span>
            <v-select
              v-model="pageNum"
              class="journal-lessons-navigation__page-size"
              aria-label="Кількість стовпців журналу"
              :disabled="operationInProgress"
              :items="pageItems"
              dense
              hide-details
              outlined
            ></v-select>
            <button
              type="button"
              class="journal-lessons-navigation__arrow"
              :disabled="!hasPrevPagginationPage || operationInProgress"
              aria-label="Попередні уроки"
              @click="getPrevPage"
            >
              <v-icon size="21">mdi-chevron-left</v-icon>
            </button>
            <button
              type="button"
              class="journal-lessons-navigation__arrow"
              :disabled="!hasNextPagginationPage || operationInProgress"
              aria-label="Наступні уроки"
              @click="getNextPage"
            >
              <v-icon size="21">mdi-chevron-right</v-icon>
            </button>
            <button
              type="button"
              class="journal-lessons-navigation__range-toggle"
              :class="{
                'journal-lessons-navigation__range-toggle--active': showRangePicker,
              }"
              @click="showRangePicker = !showRangePicker"
            >
              <v-icon size="18">mdi-tune-variant</v-icon>
              Обрати діапазон
            </button>
          </div>
        </div>

        <div v-if="showRangePicker" class="journal-range-picker">
          <span>Показати уроки</span>
          <label>
            <span>з</span>
            <input
              v-model="startNumber"
              type="number"
              min="1"
              :disabled="operationInProgress"
            />
          </label>
          <label>
            <span>по</span>
            <input
              v-model="endNumber"
              type="number"
              min="1"
              :disabled="operationInProgress"
            />
          </label>
          <v-btn
            :disabled="operationInProgress"
            color="primary"
            depressed
            @click="applyRange"
          >
            Застосувати
          </v-btn>
        </div>
      </div>

      <div v-else class="journal-filter-row">
        <div class="journal-filter-column">
          <div class="journal-filter-controls d-flex justify-space-between">
            <div class="journal-color-control">
              <v-switch
                class="journal-color-switch ma-0 pa-0"
                v-model="allowColorTheme"
                hide-details
              ></v-switch>
              <span>Кольори</span>
            </div>
            <v-combobox
              class="journal-filter-select ma-0 me-3 pa-0"
              v-model="selectFilter"
              :disabled="operationInProgress"
              :items="filterItems"
              label="Фільтр"
              clearable
              dense
              hide-details
              multiple
              outlined
            ></v-combobox>
          </div>
        </div>
      </div>

      <JournalMobileView
        v-if="isTeacher && isMobileLayout"
        :rows="journalNew.displayStructure"
        :is-in-range="isColumnInRange"
        @select-column="handleMobileColumnSelection"
        @open-cell="handleMobileCellClick"
        @update-cell="updateMobileCell"
        @finish-cell="finishCellEditing"
      />

      <div
        v-else
        class="tbl"
        role="table"
        aria-label="Журнал класу"
        @mouseover="showTooltip"
        @mouseleave="hideTooltip"
        @mousemove="onMousemoveTooltip"
      >
        <div v-if="operationInProgress" class="progress-bar">
          <div class="progress-line"></div>
        </div>
        <div @click="handleClickInJournal">
          <div v-if="operationInProgress" class="progress-cicular">
            <v-progress-circular
              class="theme-loader"
              indeterminate
            ></v-progress-circular>
          </div>
          <div
            class="row"
            role="row"
            :class="{
              'grey lighten-4': indexRow % 2 === 0,
              'in-progress': operationInProgress,
            }"
            v-for="(row, indexRow) of journalNew.displayStructure"
            :style="journalNew.getJournalRowStyles()"
            style="max-width: unset;"
            :key="indexRow"
          >
            <div
              class="cell"
              :role="
                indexRow === 0
                  ? 'columnheader'
                  : item.isName
                  ? 'rowheader'
                  : 'cell'
              "
              :indexRow="indexRow"
              :indexColumn="indexColumn"
              :class="{
                'custom-size': item.isCustomHeader,
                sticky: item.sticky,
                'number-lesson': item.isNumber,
                'student-name': item.isName,
                odd: indexRow % 2 === 0,
                even: indexRow % 2 === 1,
                header: indexRow === 0,
                'in-range': journalNew.isInRange(indexColumn),
                'grade-cell': indexRow > 0 && indexColumn > 1,
                'grade-cell--empty':
                  indexRow > 0 && indexColumn > 1 && item.label === '-',
              }"
              v-for="(item, indexColumn) of row"
              :key="indexColumn"
              :row-index="indexColumn"
              :tooltip-label="item.lessonName"
            >
              <div
                v-if="item.edit && isTeacher"
                class="edit-filde"
                :class="[
                  {
                    'label-wrapper':
                      item.cellType === 'cell' && indexColumn > 1,
                    'right-margin': item.type === addItemsEnum.AUDIATION,
                  },
                ]"
              >
                <input
                  class="edit-input set-mark"
                  :class="{
                    invalid:
                      item.label &&
                      item.label.length !== 0 &&
                      !markRule(item.label),
                  }"
                  v-model="item.label"
                  type="text"
                  aria-label="Редагування оцінки"
                  title="Введіть оцінку від 1 до 12. Enter — зберегти."
                  @input="updateItemVal(item)"
                  @blur="finishCellEditing(item)"
                  @keydown.enter.prevent="finishCellEditing(item)"
                />
              </div>
              <div
                :class="{
                  'header-cell-label': indexRow === 0,
                }"
                class="cell-label"
                v-else
              >
                <span v-if="item.type === addItemsEnum.AUDIATION">
                  <input
                    class="edit-input"
                    v-model="item.label"
                    type="text"
                    :disabled="!isTeacher"
                    aria-label="Назва стовпчика"
                    title="Назва стовпчика"
                    @input="updateNameVal(item)"
                    @keydown.enter.prevent="$event.target.blur()"
                  />
                </span>
                <span
                  v-else
                  :class="[
                    {
                      'label-wrapper':
                        item.cellType === 'cell' &&
                        (indexColumn > 1 || !isTeacher),
                      'theme-wrapper':
                        (item.type === addItemsEnum.THEME ||
                          item.type === addItemsEnum.SEMESTERS) &&
                        item.cellType === 'header',
                    },
                    getLabelClass(item.label),
                  ]"
                >
                  {{ item.label }}
                </span>
                <span
                  class="dot-wrapper"
                  v-if="
                    [addItemsEnum.THEME].indexOf(item.type) !== -1 &&
                    indexRow === 0 &&
                    isTeacher
                  "
                >
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <div class="dots-menu"></div>
                        <div class="dots-menu"></div>
                        <div class="dots-menu"></div>
                      </span>
                    </template>
                    <v-list>
                      <v-list-item
                        v-for="(menuItem, index) in menuItems"
                        @click="handleThemeItem(item, menuItem.value)"
                        :key="index"
                      >
                        <v-list-item-title class="menu-item">{{
                          menuItem.title
                        }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </span>
                <span
                  class="lesson-remove-ico"
                  v-if="
                    [addItemsEnum.AUDIATION, addItemsEnum.SEMESTERS].indexOf(
                      item.type
                    ) !== -1 &&
                    indexRow === 0 &&
                    isTeacher
                  "
                >
                  <svg
                    name="remove-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 3V4H4V6H5V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V6H20V4H15V3H9ZM7 6H17V19H7V6ZM9 8V17H11V8H9ZM13 8V17H15V8H13Z"
                      fill="#757575"
                    />
                  </svg>
                </span>
                <span v-if="item.cellType === 'header' && item.isName">
                  <svg
                    :class="item.sortDirection === 1 ? 'up' : 'down'"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7 14L12 19L17 14H7Z" fill="#757575" />
                    <rect x="11" y="5" width="2" height="9" fill="#757575" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import JournalEventEnum from '@/enums/JournalEventEnum';
import PopupEventEnum from '@/enums/PopupEventEnum';
import JournalTaskEnum from '@/enums/JournalTaskEnum';

import ConfirmPopup from '@/components/View/ConfirmPopup/ConfirmPopup';
import JournalColumnEditor from '@/components/View/TeacherJournal/JournalColumnEditor';
import JournalMobileView from '@/components/View/TeacherJournal/JournalMobileView';

const addItemsEnum = {
  NAME: 'name',
  LESSON: 0,
  AUDIATION: 1,
  THEME: 2,
  SEMESTERS: 3,
  ANNUAL: 4,
  CORRECTION: 5,
  THEME_CORRECTION: 6,
};

const cellTypeEnum = {
  HEADER: 'header',
  CELL: 'cell',
};

class BaseCell {
  constructor(type) {
    this.edit = false;
    this.isCustomHeader = ![addItemsEnum.LESSON, addItemsEnum.NAME].includes(
      type
    );
  }
  isEditable() {
    return false;
  }
}

class Cell extends BaseCell {
  constructor(label, id, type, cellType, sticky, isNumber, isName) {
    super(type);
    this.label = label;
    this.id = id;
    this.type = type;
    this.cellType = cellType;
    this.edit = [
      addItemsEnum.AUDIATION,
      addItemsEnum.CORRECTION,
      addItemsEnum.THEME_CORRECTION,
    ].includes(type);
    this.sticky = sticky;
    this.isNumber = isNumber;
    this.isName = isName;
  }

  updateLabel(val) {
    this.label = val;
  }

  toggleEdit() {
    this.edit = !this.edit;
  }

  isEditable() {
    return [
      addItemsEnum.AUDIATION,
      addItemsEnum.CORRECTION,
      addItemsEnum.THEME_CORRECTION,
    ].includes(this.type);
  }
}

class Header extends BaseCell {
  constructor(label, lessonName, id, type, cellType, sticky, isNumber, isName) {
    super(type);
    this.label = label;
    this.lessonName = lessonName;
    this.id = id;
    this.type = type;
    this.cellType = cellType;
    this.sticky = sticky;
    this.sortDirection = -1;
    this.isNumber = isNumber;
    this.isName = isName;
  }
}

class Journal {
  constructor(structure, isTeacher) {
    const defaulCurrentPage = {
      label: 1,
      index: 0,
    };
    this.isTeacher = isTeacher;
    this.rawStructure = structure;
    this.structure = structure;
    this.displayStructure = structure;
    this.NUMBER_TECHNICAL_COLS = isTeacher ? 2 : 0;
    this.numberRows = this.displayStructure[0]
      ? this.displayStructure[0].length
      : 0;
    this.pages = [];
    this.defaulCurrentPage = defaulCurrentPage;
    this.currentPage = defaulCurrentPage;

    this.themeRange = {
      start: -1,
      end: -1,
    };

    const customCell = 152;
    this.cellSizeMap = {
      [addItemsEnum.NAME]: 156, //becouse this.nameSize  divided 2
      [addItemsEnum.LESSON]: 74,
      [addItemsEnum.AUDIATION]: customCell,
      [addItemsEnum.THEME]: customCell,
      [addItemsEnum.SEMESTERS]: customCell,
      [addItemsEnum.ANNUAL]: customCell,
      [addItemsEnum.CORRECTION]: customCell,
      [addItemsEnum.THEME_CORRECTION]: customCell,
    };
    this.cellSize = 74;
    this.nameSize = isTeacher ? 312 : 0;
    this.customCell = 152;

    this.numberOnePage = 10;
    this.numberTwoPage = 20;
  }

  sortColumn(columnIndex, rowIndex) {
    const header = this.displayStructure[rowIndex][columnIndex];
    header.sortDirection = header.sortDirection === 1 ? -1 : 1;
    this.displayStructure[0].forEach((cell, index) => {
      if (index !== columnIndex) {
        cell.sortDirection = 1;
      }
    });
    this.displayStructure.sort((rowA, rowB) => {
      const cellA = rowA[columnIndex];
      const cellB = rowB[columnIndex];
      if (
        cellA.cellType === cellTypeEnum.HEADER ||
        cellB.cellType === cellTypeEnum.HEADER
      ) {
        return 1;
      }
      const valA = cellA.label;
      const valB = cellB.label;
      if (!valA || !valB) {
        return 1;
      }
      if (header.sortDirection === 1) {
        return valA > valB ? 1 : -1;
      }
      return valA > valB ? -1 : 1;
    });
  }

  isEmpty() {
    return this.rawStructure.length === 0;
  }

  calcPages(pageWidh) {
    const numberInPage = Math.round((pageWidh - this.nameSize) / this.cellSize);
    this.numberOnePage = numberInPage;
    this.numberTwoPage = numberInPage * 2;
  }

  getDisplayCell(indexRow, indexColumn) {
    return this.displayStructure[indexRow][indexColumn];
  }

  search(payload) {
    const { types, name } = payload;
    const searchVal = name.toLowerCase();
    this.structure = this.rawStructure.reduce((newStructure, row) => {
      const nameItem = row[1];
      const validRow =
        nameItem.type === addItemsEnum.NAME &&
        nameItem.cellType !== cellTypeEnum.HEADER
          ? nameItem.label.toLowerCase().indexOf(searchVal) !== -1
          : true;
      if (!validRow) {
        return newStructure;
      }
      let filtred = row;
      if (types.length !== 1) {
        filtred = row.filter((item) => {
          return types.includes(item.type);
        });
      }
      newStructure.push(filtred);
      return newStructure;
    }, []);
    const startPage = 0;
    const filtredNumber = this.structure[0].length;
    this._sliceStructure(filtredNumber, startPage);
  }

  isInRange(indexColumn) {
    const inRange =
      this.themeRange.start <= indexColumn &&
      indexColumn <= this.themeRange.end;
    return inRange;
  }

  removeHighlight() {
    this.themeRange.start = -1;
    this.themeRange.end = -1;
  }

  startSelectHeaderByType(rowIndex, type) {
    switch (type) {
      case addItemsEnum.AUDIATION:
        this.selectAudition(rowIndex);
        break;
      case addItemsEnum.THEME:
        this.selectTheme(rowIndex);
        break;
      case addItemsEnum.SEMESTERS:
        this.selectSemest(rowIndex);
        break;
      default:
        this.selectStratagy(rowIndex);
        break;
    }
  }

  selectAudition(rowIndex) {
    this.updateStartThemeRange(rowIndex);
    this.updateEndThemeRange(rowIndex);
  }

  selectTheme(rowIndex) {
    // const headers = this.displayStructure[0];
    // let end = this.NUMBER_TECHNICAL_COLS;
    // for (let i = rowIndex; i > 0; i--) {
    //   const header = headers[i];
    //   if (
    //     [
    //       addItemsEnum.THEME,
    //       addItemsEnum.CORRECTION,
    //       addItemsEnum.THEME_CORRECTION,
    //       addItemsEnum.SEMESTERS,
    //     ].includes(header.type)
    //   ) {
    //     end = i + 1;
    //     break;
    //   }
    // }

    // this.updateStartThemeRange(Math.max(end, this.NUMBER_TECHNICAL_COLS));
    this.updateStartThemeRange(rowIndex);
    this.updateEndThemeRange(rowIndex);
  }

  selectSemest(rowIndex) {
    const headers = this.displayStructure[0];
    const selectHeader = headers[rowIndex];
    if (selectHeader.type !== addItemsEnum.THEME) {
      return;
    }
    const end = this.NUMBER_TECHNICAL_COLS;

    this.updateStartThemeRange(Math.max(end, this.NUMBER_TECHNICAL_COLS));
    this.updateEndThemeRange(rowIndex);
  }

  selectStratagy(rowIndex) {
    if (this.themeRange.start === rowIndex) {
      this.updateStartThemeRange(-1);
      return;
    }

    if (this.themeRange.start === -1) {
      this.updateStartThemeRange(rowIndex);
      return;
    }

    if (this.themeRange.start < rowIndex) {
      this.updateEndThemeRange(rowIndex);
    } else {
      const start = this.themeRange.start;
      this.updateStartThemeRange(rowIndex);
      this.updateEndThemeRange(start);
    }
  }

  getHeaderByRowIndex(rowIndex) {
    const headers = this.displayStructure[0];
    return headers[rowIndex];
  }

  getLessonIdsFromSelectedRange() {
    const selectedLessons = this._getSelectedRange();
    return selectedLessons.map((item) => item.id);
  }

  getDeleteLessonIdsFromSelectedRange() {
    const selectedLessons = this._getSelectedRange();
    const allowDeleteTypes = [
      addItemsEnum.AUDIATION,
      addItemsEnum.THEME,
      addItemsEnum.SEMESTERS,
      addItemsEnum.ANNUAL,
    ];
    return selectedLessons.reduce((ids, item) => {
      if (allowDeleteTypes.includes(item.type)) {
        ids.push(item.id);
      }
      return ids;
    }, []);
  }

  _getSelectedRange() {
    if (this.themeRange.start === -1 || this.themeRange.end === -1) {
      return [];
    }
    const technicalCol = 0;
    const headers = this.displayStructure[0];
    const isLast = headers.length - 1 === this.themeRange.start;
    const begin = isLast
      ? this.themeRange.start
      : this.themeRange.start + technicalCol;

    const end =
      this.themeRange.end === this.themeRange.start
        ? this.themeRange.end + 1
        : this.themeRange.end + 1;

    const selectedLessons = headers.slice(begin, end + technicalCol);

    return selectedLessons;
  }

  updateEndThemeRange(val) {
    this.themeRange.end = val;
  }

  updateStartThemeRange(val) {
    this.themeRange.start = val;
    this.themeRange.end = val;
  }

  addColumn(colIndex, type) {
    this.structure = this.structure.reduce((newStructure, row, index) => {
      const itemType = index === 0 ? addItemsEnum.HEADER : addItemsEnum.LESSON;
      const cell = this.createCellByType(type, itemType);
      const newRow = [...row];
      newRow.splice(colIndex + 1, 0, cell);
      newStructure.push(newRow);
      return newStructure;
    }, []);
    const startPage = 0;
    this._sliceStructure(this.numberRows, startPage);
  }

  createCellByType(cellType, itemType) {
    switch (cellType) {
      case addItemsEnum.AUDIATION: {
        const label = '';
        const id = '';
        return new Cell(label, id, itemType, addItemsEnum.AUDIATION);
      }
    }
  }

  hasPrev() {
    return this.currentPage.index !== 0;
  }

  hasNext() {
    return (
      this.pages.length !== 0 &&
      this.currentPage.index !== this.pages.length - 1
    );
  }

  setOnePageMode() {
    const startPage = 0;
    this._sliceStructure(
      this.numberOnePage + this.NUMBER_TECHNICAL_COLS,
      startPage
    );
  }

  setTwoPageMode() {
    const startPage = 0;
    this._sliceStructure(
      this.numberTwoPage + this.NUMBER_TECHNICAL_COLS,
      startPage
    );
  }

  setAll() {
    this.displayStructure = this.structure;
    this.numberRows = this.displayStructure[0].length;
  }

  getAddSelectedRangeLabels() {
    const { start, end } = this.themeRange;
    const structure = this.displayStructure[0];
    const startLabel = structure[start] ? structure[start].label : '';
    const endLabel = structure[end] ? structure[end].label : '';
    return {
      startLabel,
      endLabel,
    };
  }

  getPages() {
    let numberPages = Math.ceil(
      (this.structure[0].length - this.NUMBER_TECHNICAL_COLS) /
        (this.numberRows - this.NUMBER_TECHNICAL_COLS)
    );
    numberPages = isNaN(numberPages) ? 0 : numberPages;
    const pages = [];
    for (let index = 0; index < numberPages; index++) {
      pages.push({
        label: index + 1,
        index: index,
      });
    }
    this.pages = pages;
    this.currentPage = pages[0] || this.defaulCurrentPage;
    return this.pages;
  }

  navidateToPage(page) {
    this.currentPage = page;
    this._sliceStructure(this.numberRows, page.index);
  }

  moveStartPoistion() {}

  _sliceStructure(number, startPage) {
    const displayStructure = [];
    this.numberRows = number;

    const delta = this.numberRows - this.NUMBER_TECHNICAL_COLS;

    const start =
      startPage === 0
        ? this.NUMBER_TECHNICAL_COLS
        : startPage * delta + this.NUMBER_TECHNICAL_COLS;

    const end = start + delta;
    for (let index = 0; index < this.structure.length; index++) {
      const row = this.structure[index];
      const slicedRow = [];
      if (this.isTeacher) {
        slicedRow = [row[0], row[1]];
      }
      [].push.apply(slicedRow, [...row.slice(start, end)]);
      displayStructure.push(slicedRow);
    }
    this.displayStructure = displayStructure;
  }

  getJournalRowStyles() {
    const headers = this.displayStructure[0];

    const calcWidth = headers.reduce((calcWidth, header) => {
      const size = this.cellSizeMap[header.type] || this.customCell;
      calcWidth += size;
      return calcWidth;
    }, 0);
    const width =
      (this.numberRows - this.NUMBER_TECHNICAL_COLS) * this.cellSize +
      this.nameSize;
    return `width:${calcWidth}px`;
  }
}

function _getCellDefaultValByType(type, evaluationItem) {
  const defaultVal =
    evaluationItem.status === 3 && !evaluationItem.evaluation ? '+' : '-';
  return [
    addItemsEnum.CORRECTION,
    addItemsEnum.THEME_CORRECTION,
    addItemsEnum.AUDIATION,
  ].includes(type)
    ? ''
    : defaultVal;
}

const addItems = [
  { label: 'Тематична', type: addItemsEnum.THEME },
  { label: 'Семестрова', type: addItemsEnum.SEMESTERS },
  { label: 'Інше', type: addItemsEnum.AUDIATION },
];

const themeMenuEnum = {
  EDIT_NAME: 'editName',
  CALCULATE: 'calculate',
  DELETE: 'delete',
};

export default {
  name: 'TeacherJournal',
  components: {
    ConfirmPopup,
    JournalColumnEditor,
    JournalMobileView,
  },
  props: {
    journalTask: String,
    isTeacher: Boolean,
    sourceStore: String,
  },
  data() {
    const emptyJournal = this.buildJournalStructure({});
    return {
      showThemeMenu: false,
      themeActivator: null,
      menuItems: [
        // { title: 'Редагувати назву', value: themeMenuEnum.EDIT_NAME },
        { title: 'Розрахувати', value: themeMenuEnum.CALCULATE },
        { title: 'Видалити', value: themeMenuEnum.DELETE },
      ],
      showConfirmPopup: false,
      currentPopupContext: {},
      loaderInterval: null,
      infoTooltipClass: 'info-tooltip-show',
      infoTooltip: null,
      infoTooltipX: 0,
      infoTooltipY: 0,
      infoTooltipText: '',

      updateItemtimerId: null,
      updateItem: {},
      inputRules: [this.markRule],
      addItemsEnum,
      tooltipClass: 'tooltip-hide',
      tooltipText: '',
      allowColorTheme: false,
      addItems,
      selectedType: null,
      updateInProgress: false,
      searchByName: '',
      pageItems: [10, 20, 50, 80, 110, 140, 170, 200],
      filterItems: [
        { text: 'Уроки', value: addItemsEnum.LESSON },
        { text: 'Тематичні', value: addItemsEnum.THEME },
        { text: 'Семестрові', value: addItemsEnum.SEMESTERS },
        { text: 'Річні', value: addItemsEnum.ANNUAL },
      ],
      selectedItem: addItems[0],
      editMode: false,
      filterBtnTTip: false,
      tooltipX: 0,
      tooltipY: 0,
      journalNew: emptyJournal,
      pages: [],
      showNavigationControls: false,
      showRangePicker: false,
    };
  },
  computed: {
    isMobileLayout() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    operationInProgress: {
      get() {
        return this.$store.getters[`${this.sourceStore}/isLoading`];
      },
      set(val) {
        this.$store.commit(`${this.sourceStore}/journalLoading`, val);
      },
    },

    pageNum: {
      get() {
        return this.$store.getters[`${this.sourceStore}/getNumberLessons`];
      },
      set(val) {
        this.$store.commit(`${this.sourceStore}/setNumberLessons`, val);
      },
    },
    selectFilter: {
      get() {
        const selected = this.$store.getters[
          `${this.sourceStore}/selecteFilter`
        ];
        return selected;
      },
      set(val) {
        this.$store.commit(`${this.sourceStore}/setSelecteFilter`, val);
      },
    },
    hasJournal() {
      return Object.keys(this.journalNew).length !== 0;
    },
    journalData() {
      const journal = this.$store.getters[`${this.sourceStore}/getJournal`];
      return journal;
    },
    hasSelectedRange() {
      const {
        startLabel,
        endLabel,
      } = this.journalNew.getAddSelectedRangeLabels();
      return Boolean(startLabel) && Boolean(endLabel);
    },
    lessonRange() {
      return this.journalNew.getAddSelectedRangeLabels();
    },
    addColumnInstruction() {
      if (!this.selectedType) return '';
      switch (this.selectedType.type) {
        case addItemsEnum.THEME:
          return 'Оберіть останній урок теми.';
        case addItemsEnum.SEMESTERS:
          return 'Оберіть останню тематичну оцінку першого семестру.';
        case addItemsEnum.AUDIATION:
          return 'Оберіть стовпчик, після якого потрібно вставити новий.';
        default:
          return 'Оберіть позицію нового стовпчика.';
      }
    },
    selectedRangeDescription() {
      const { startLabel, endLabel } = this.lessonRange;
      if (!startLabel || !endLabel) return 'ще не вибрано';
      if (
        this.selectedType &&
        this.selectedType.type === addItemsEnum.AUDIATION
      ) {
        return `після «${endLabel}»`;
      }
      if (startLabel === endLabel) return `до «${endLabel}»`;
      return `від «${startLabel}» до «${endLabel}»`;
    },

    hasPrevPagginationPage() {
      const { start } = this.pagginationRange;
      return start !== 0;
    },
    hasNextPagginationPage() {
      const { end, totalLessons } = this.pagginationRange;
      return end !== totalLessons;
    },
    pagginationRange() {
      return this.$store.getters[`${this.sourceStore}/getLessonRange`];
    },
    rangeLabel() {
      const { start, end, totalLessons } = this.pagginationRange;
      return `${start + 1}-${end} з ${totalLessons} стовпчиків`;
    },
    isLoading() {
      return this.$store.getters[`${this.sourceStore}/isLoading`];
    },
    startNumber: {
      get() {
        const start = this.$store.getters[`${this.sourceStore}/getStartLesson`];
        return start + 1;
      },
      set(val) {
        const rawNum = parseInt(val) - 1;
        const num = isNaN(rawNum) ? '' : rawNum;
        this.$store.commit(`${this.sourceStore}/setStart`, num);
      },
    },
    endNumber: {
      get() {
        const end = this.$store.getters[`${this.sourceStore}/getEndLesson`];
        return end;
      },
      set(val) {
        const rawNum = parseInt(val);
        const num = isNaN(rawNum) ? '' : rawNum;
        this.$store.commit(`${this.sourceStore}/setEnd`, num);
      },
    },
  },
  watch: {
    journalTask(newTask) {
      switch (newTask) {
        case JournalTaskEnum.REFRESH: {
          this.refreshJournal();
          this.emit(JournalEventEnum.REFRESHED_JOURNAL);
          break;
        }
      }
    },
    journalData(newDate) {
      this.$_updateJournal(newDate);
    },
    searchByName() {
      this.changeFilter();
    },
    // startNumber(newStart) {
    //   const start = parseInt(newStart, 10);
    //   if (!isNaN(start)) {
    //     this.journalNew.moveStartPoistion(start);
    //   }
    // },
    async pageNum() {
      if (this.operationInProgress) {
        return;
      }
      this.$_startLoader();
      try {
        await this.$store.dispatch(`${this.sourceStore}/applyPageNumber`);
      } catch (error) {
        console.log(error);
      }
      this.$_finishLoader();
    },
    async selectFilter() {
      if (this.operationInProgress) {
        return;
      }
      this.$_startLoader();
      try {
        await this.$store.dispatch(`${this.sourceStore}/loadJournal`);
      } catch (error) {
        console.log(error);
      }
      this.$_finishLoader();
    },
  },
  async created() {},
  async mounted() {
    if (this.journalData) {
      this.$_updateJournal(this.journalData);
    }
  },
  destroyed() {
    this.$_finishLoader();
  },
  methods: {
    $_updateJournal(newDate) {
      if (this.updateInProgress) {
        return;
      }
      this.updateInProgress = true;

      this.journalNew = this.buildJournalStructure(
        newDate,
        this.pagginationRange
      );
      setTimeout(() => {
        try {
          const journalSelector = '#journal';
          const journal = document.querySelector(journalSelector);
          if (!journal) {
            console.log(
              `Does not find journal element by selector ${journalSelector}`
            );
            return;
          }
          this.journalNew.calcPages(journal.offsetWidth);
        } catch (error) {
          console.log(`get error on create journal error: ${error}`);
        }

        this.updateInProgress = false;
      }, 100);
    },
    async applyRange() {
      if (this.operationInProgress) {
        return;
      }
      this.$_startLoader();
      try {
        await this.$store.dispatch(`${this.sourceStore}/applyCustomRange`);
      } catch (error) {
        console.log(error);
      }
      this.$_finishLoader();
    },
    async getPrevPage() {
      if (this.operationInProgress) {
        return;
      }
      this.$_startLoader();
      try {
        await this.$store.dispatch(`${this.sourceStore}/applyPrevRange`);
      } catch (error) {
        console.log(error);
      }
      this.$_finishLoader();
    },
    async getNextPage() {
      if (this.operationInProgress) {
        return;
      }
      this.$_startLoader();
      try {
        await this.$store.dispatch(`${this.sourceStore}/applyNextRange`);
      } catch (error) {
        console.log(error);
      }
      this.$_finishLoader();
    },
    getLabelClass(label) {
      if (!this.allowColorTheme) {
        return;
      }
      const val = parseInt(label, 10);
      if (val < 4) {
        return 'red-val';
      }
      if (val >= 4 && val < 7) {
        return 'yellow-val';
      }
      if (val >= 7 && val < 10) {
        return 'blue-val';
      }
      if (val >= 10) {
        return 'green-val';
      }
      return '';
    },
    popupEventHandler(payload) {
      const { type, data } = payload;
      switch (type) {
        case PopupEventEnum.CLOSE_POPUP:
          this.showConfirmPopup = false;
          this.currentPopupContext = {};
          break;
        case PopupEventEnum.CONFIRM:
          this.showConfirmPopup = false;
          this.currentPopupContext = {};
          this.removeColumnServer(data);
        case PopupEventEnum.DECLINE:
          this.showConfirmPopup = false;
          this.currentPopupContext = {};
        default:
          break;
      }
    },
    openConfirm(item) {
      this.currentPopupContext = {
        message: 'Видалити стовпчик',
        item,
      };
      this.showConfirmPopup = true;
    },
    handleThemeItem(cell, action) {
      switch (action) {
        case themeMenuEnum.EDIT_NAME:
          this.$_editCell(cell);
          break;
        case themeMenuEnum.CALCULATE:
          this.$_calcTheme(cell);
          break;
        case themeMenuEnum.DELETE:
          this.removeColumnServer(cell);
          break;
      }
    },
    openThemeMenu(targetElement) {
      this.showThemeMenu = true;
      setTimeout(() => {
        this.themeActivator = targetElement;
      }, 100);
    },
    updateItemVal(item) {
      clearTimeout(this.updateItemtimerId);
      if (!this.markRule(item.label)) {
        return;
      }
      const id = this.updateItem.id;
      if (id && id !== item.id) {
        this.putToJournal(this.updateItem.id, this.updateItem.label);
      }
      this.updateItem = item;
      this.updateItemtimerId = setTimeout(() => {
        this.putToJournal(item.id, item.label);
      }, 1000);
    },
    finishCellEditing(item) {
      if (!item || !item.edit || !this.markRule(item.label)) {
        return;
      }
      clearTimeout(this.updateItemtimerId);
      this.putToJournal(item.id, item.label);
      this.updateItem = {};
      item.toggleEdit();
    },
    isColumnInRange(indexColumn) {
      return this.journalNew.isInRange(indexColumn);
    },
    handleMobileColumnSelection(indexColumn) {
      if (!this.selectedType) return;
      this.journalNew.startSelectHeaderByType(
        indexColumn,
        this.selectedType.type
      );
    },
    handleMobileCellClick({ item }) {
      if (item.isEditable() && !item.edit) {
        item.toggleEdit();
        return;
      }
      const isLabelNumber = !isNaN(parseInt(item.label, 10));
      if (
        isLabelNumber &&
        item.id &&
        !item.edit &&
        item.type === addItemsEnum.LESSON
      ) {
        this.emit(JournalEventEnum.OPEN_LESSON, { value: item.id });
      }
    },
    updateMobileCell(item, value) {
      item.updateLabel(value);
      this.updateItemVal(item);
    },
    async putToJournal(lessonId, val) {
      if (this.operationInProgress) {
        return;
      }
      try {
        this.$_startLoader();
        let totalEvaluation = parseInt(val, 10);
        totalEvaluation = isNaN(totalEvaluation) ? 0 : totalEvaluation;
        await this.$store.dispatch('TeacherRoomStore/putToJournal', {
          lessonId,
          totalEvaluation,
        });
      } catch (error) {
        console.error(`get error on put journal error:${error}`);
      }
      this.$_finishLoader();
    },
    markRule(val) {
      const parsed = parseInt(val);
      return /^\d+$/.test(val) ? parsed > 0 && parsed < 13 : false;
    },
    isValidLessonVal(val) {
      const parsed = parseInt(val);
      return /^\d+$/.test(val) ? parsed > 0 && parsed < 10000 : false;
    },
    updateNameVal(item) {
      clearTimeout(this.updateItemtimerId);
      this.updateItemtimerId = setTimeout(() => {
        this.updateColumn(item.id, item.label, item.type);
      }, 1000);
    },
    selectType(item) {
      if (this.selectedType && this.selectedType.type !== item.type) {
        this.journalNew.removeHighlight();
      }
      this.selectedType = item;
      this.hideInfoTooltip();
    },
    addCol() {
      this.hideInfoTooltip();
    },
    hideInfoTooltip() {
      this.infoTooltipClass = 'info-tooltip-hide';
      this.infoTooltip = false;
      this.infoTooltipText = '';
    },
    showInfoToolTip(item) {
      const table = document.querySelector('.tbl');
      const rect = table.getBoundingClientRect();
      const { text, x, y } = this.$_getTooltipInfoByType(item, rect);
      this.infoTooltipClass = 'info-tooltip-show';
      this.infoTooltipX = x + rect.width / 2;
      this.infoTooltipY = y + 64;
      this.infoTooltipText = text;
      this.infoTooltip = true;
    },
    $_getTooltipInfoByType(item, rect) {
      switch (item.type) {
        case addItemsEnum.THEME:
          return {
            text: 'Оберіть останній стовпчик теми',
            x: rect.x,
            y: rect.y,
          };
        case addItemsEnum.SEMESTERS:
          return {
            text:
              'Оберіть останню тематичну ПЕРШОГО семестру. Семестр 2 та Річна будуть створені автоматично',
            x: rect.x,
            y: rect.y,
          };
        case addItemsEnum.AUDIATION:
          return {
            text: 'Оберіть стовпчик, після якого бажаєте додати новий',
            x: rect.x,
            y: rect.y,
          };
      }
      return '';
    },
    buildJournalStructure(journal, pagginationRange) {
      const structure = this._getJournalStructure(
        journal,
        this.isTeacher,
        pagginationRange
      );
      return new Journal(structure, this.isTeacher);
    },
    _getJournalStructure(journal, isTeacher, pagginationRange) {
      const journalStructure = [];
      if (!journal || Object.keys(journal).length === 0) {
        return journalStructure;
      }
      let headers = [];
      if (isTeacher) {
        const sticky = true;

        headers = [
          this.$_headerItem(
            '№ з/п',
            '',
            '',
            addItemsEnum.NAME,
            sticky,
            true,
            false
          ),
          this.$_headerItem(
            'Прізвище та ім’я учня',
            '',
            '',
            addItemsEnum.NAME,
            sticky,
            false,
            true
          ),
        ];
      }
      const { start } = pagginationRange;
      const idexSummary = {
        [addItemsEnum.LESSON]: (start || 0) + 1,
        [addItemsEnum.THEME]: 1,
        [addItemsEnum.SEMESTERS]: 1,
        [addItemsEnum.ANNUAL]: 1,
      };
      const lessonTypeIndexMap = {};
      journal.lessons.forEach((lesson, index) => {
        lessonTypeIndexMap[index] = lesson.type;
        if (lesson.type === addItemsEnum.CORRECTION && !this.isTeacher) {
          return;
        }
        const label = this.$_getLabelByType(lesson, idexSummary);
        headers.push(
          this.$_headerItem(label, lesson.id, lesson.name, lesson.type)
        );
      });
      journalStructure.push(headers);
      journal.students.sort((studentA, studentB) => {
        const nameA = `${studentA.last_name} ${studentA.first_name}`;
        const nameB = `${studentB.last_name} ${studentB.first_name}`;
        return nameA > nameB ? -1 : 1;
      });
      journal.students.forEach((student, index) => {
        let row = [];
        if (isTeacher) {
          const sticky = true;
          row.push(
            this.$_rowItem(
              index + 1,
              '',
              addItemsEnum.NAME,
              sticky,
              true,
              false
            )
          );
          row.push(
            this.$_rowItem(
              `${student.last_name} ${student.first_name}`,
              student.id,
              addItemsEnum.NAME,
              sticky,
              false,
              true
            )
          );
        }

        student.evaluations.forEach((evaluationItem, index) => {
          const type = lessonTypeIndexMap[index];
          if (type === addItemsEnum.CORRECTION && !this.isTeacher) {
            return;
          }
          const defaultVal = _getCellDefaultValByType(type, evaluationItem);
          row.push(
            this.$_rowItem(
              evaluationItem.evaluation || defaultVal,
              evaluationItem.lesson__id,
              type
            )
          );
        });
        journalStructure.push(row);
        row = [];
      });

      return journalStructure;
    },
    $_getLabelByType({ type, name, semester, lesson_number }, idexSummary) {
      let label = '';
      switch (type) {
        case addItemsEnum.LESSON:
          label = lesson_number
            ? `Урок ${lesson_number}`
            : `Урок ${idexSummary[type]}`;
          break;
        case addItemsEnum.THEME:
          label = name || `Тема`;
          break;
        case addItemsEnum.SEMESTERS:
          label = `Семестр ${semester}`;
          break;
        case addItemsEnum.AUDIATION:
          label = name || `Інше`;
          break;
        case addItemsEnum.ANNUAL:
          label = `Річна`;
          break;
        case addItemsEnum.CORRECTION:
          label = `Скоригована`;
          break;
        case addItemsEnum.THEME_CORRECTION:
          label = `Тем. Cкор.`;
          break;
        default:
          console.error(`get unsuported type ${type}`);
          break;
      }
      idexSummary[type] += 1;
      return label;
    },
    $_headerItem(
      label,
      id,
      name,
      type,
      sticky = false,
      isNumber = false,
      isName = false
    ) {
      return new Header(
        label,
        name,
        id,
        type,
        cellTypeEnum.HEADER,
        sticky,
        isNumber,
        isName
      );
    },
    $_rowItem(
      label,
      id,
      type,
      sticky = false,
      isNumber = false,
      isName = false
    ) {
      return new Cell(
        label,
        id,
        type,
        cellTypeEnum.CELL,
        sticky,
        isNumber,
        isName
      );
    },
    setOnePageMode() {
      this.journalNew.setOnePageMode();
      this._calcNumberPages();
      this.showNavigationControls = true;
    },

    setTwoPageMode() {
      this.journalNew.setTwoPageMode();
      this._calcNumberPages();
      this.showNavigationControls = true;
    },
    setAll() {
      this.journalNew.setAll();
      this._calcNumberPages();
      this.showNavigationControls = false;
    },

    _calcNumberPages() {
      this.pages = this.journalNew.getPages();
    },

    navidateToPage(page) {
      this.journalNew.navidateToPage(page);
    },

    addColumn() {
      this.journalNew.addColumn(0, addItemsEnum.AUDIATION);
    },

    cancelAddColumn() {
      this.selectedType = null;
      this.hideInfoTooltip();
      this.journalNew.removeHighlight();
    },

    async addColumnServer() {
      try {
        if (this.operationInProgress) {
          return;
        }
        const teacherClass = this.$store.getters[
          'TeacherRoomStore/getTeacherClass'
        ];
        const studentGroupeId = this.$store.getters[
          'TeacherRoomStore/getStudentGroupe'
        ];
        const themelessonsIds = this.journalNew.getLessonIdsFromSelectedRange();
        const hasSelectedType = this.selectedType && this.selectedType.type;
        if (themelessonsIds.length === 0 || !hasSelectedType) {
          console.error(
            `Get invalid data ${themelessonsIds} ${this.selectedType}`
          );
          return;
        }
        this.$_startLoader();
        await this.$store.dispatch('TeacherRoomStore/addColumn', {
          student_group: studentGroupeId,
          course: teacherClass.id,
          semester: 0,
          type: this.selectedType.type,
          name: '',
          description: '',
          themelessons: themelessonsIds,
        });

        await this.$store.dispatch('TeacherRoomStore/loadJournal');
        this.cancelAddColumn();
      } catch (error) {
        console.error(`get error on add column ${error}`);
      }
      this.$_finishLoader();
    },

    $_getLoaderEWlement() {
      return document.querySelector('.progress-line');
    },

    $_updateProgressWidth(percent) {
      const element = this.$_getLoaderEWlement();
      if (element) {
        element.style.width = `${percent}%`;
      }
    },

    $_startLoader() {
      this.operationInProgress = true;
      clearInterval(this.loaderInterval);
      const duration = 3 * 1000;
      const updateTime = 20;
      const maxPercents = 90;
      const delta = Math.round(maxPercents / (duration / updateTime));
      let startVal = 0;
      this.loaderInterval = setInterval(() => {
        this.$_updateProgressWidth(startVal);
        startVal += delta;
      }, 100);
    },

    $_finishLoader() {
      this.operationInProgress = false;
      this.$_updateProgressWidth(100);
      clearInterval(this.loaderInterval);
      setTimeout(() => {
        this.$_clearLoader();
      }, 400);
    },

    $_clearLoader() {
      this.$_updateProgressWidth(0);
      clearInterval(this.loaderInterval);
    },

    async updateColumn(id, name, type) {
      const teacherClass = this.$store.getters[
        'TeacherRoomStore/getTeacherClass'
      ];
      const studentGroupeId = this.$store.getters[
        'TeacherRoomStore/getStudentGroupe'
      ];

      const data = {
        student_group: studentGroupeId,
        course: teacherClass.id,
        semester: 0,
        type: type,
        name: name,
        description: '',
        themelessons: [],
      };

      await this.$store.dispatch('TeacherRoomStore/updateColumn', { id, data });
    },
    $_editCell() {
      console.log('need implement edit cell');
    },
    async $_calcTheme(cell) {
      try {
        if (this.operationInProgress) {
          return;
        }
        this.$_startLoader();
        const calcIds = cell.id;
        if (!calcIds) {
          return;
        }
        await this.$store.dispatch('TeacherRoomStore/calculateTheme', {
          id: cell.id,
        });
        await this.$store.dispatch('TeacherRoomStore/loadJournal');
      } catch (error) {
        console.error(`get error on remove column error ${error}`);
      }
      this.$_finishLoader();
    },
    async removeColumnServer(item) {
      try {
        if (this.operationInProgress) {
          return;
        }
        this.$_startLoader();
        const deleteLessonsId = item.id;
        if (!deleteLessonsId) {
          return;
        }
        await this.$store.dispatch('TeacherRoomStore/removeColumn', {
          lessonId: deleteLessonsId,
        });
        await this.$store.dispatch('TeacherRoomStore/loadJournal');
      } catch (error) {
        console.error(`get error on remove column error ${error}`);
      }
      this.$_finishLoader();
    },

    async refreshJournal() {
      try {
        if (this.operationInProgress) {
          return;
        }
        this.$_startLoader();
        await this.$store.dispatch('TeacherRoomStore/loadJournal');
      } catch (error) {
        console.error(`get error on remove column error ${error}`);
      }
      this.$_finishLoader();
    },

    changeFilter() {
      this.pageNum = 'Всі';
      this.setAll();
      let types = [addItemsEnum.NAME];
      types = this.selectFilter.reduce((types, item) => {
        types.push(item.value);
        if (item.value === addItemsEnum.SEMESTERS) {
          types.push(addItemsEnum.CORRECTION);
        }
        if (item.value === addItemsEnum.THEME) {
          types.push(addItemsEnum.THEME_CORRECTION);
        }
        return types;
      }, types);

      this.journalNew.search({
        types,
        name: this.searchByName,
      });
    },

    showTooltip(event) {
      if (this.selectedType !== null) {
        return;
      }
      this.filterBtnTTip = true;
    },
    hideTooltip() {
      if (this.selectedType !== null) {
        return;
      }
      this.filterBtnTTip = false;
    },
    onMousemoveTooltip(event) {
      if (!event || !event.target || this.selectedType !== null) {
        return;
      }

      const isHeader = event.target.classList.contains('header-cell-label');
      const isParentHeader = event.target.parentElement.classList.contains(
        'header-cell-label'
      );
      if (!isHeader && !isParentHeader) {
        this.$_dropTooltipPosition();
        return;
      }
      const header = event.target.closest('.header');
      if (!header) {
        this.$_dropTooltipPosition();
        return;
      }
      const text = header.getAttribute('tooltip-label');
      if (!text) {
        this.$_dropTooltipPosition();
        return;
      }
      this.tooltipText = text;
      this.tooltipX = event.screenX;
      this.tooltipY = event.screenY - 100;

      this.tooltipClass = 'tooltip-show';
      this.showTooltip();
      return;
    },
    $_dropTooltipPosition() {
      this.tooltipClass = 'tooltip-hide';
      this.hideTooltip();
    },
    handleClickInJournal(event) {
      if (!event || !event.target) {
        return;
      }
      this.hideInfoTooltip();
      const { x, y } = event;
      const elements = document.elementsFromPoint(x, y);
      let icon;
      const isRemoveIcon = elements.some((element) => {
        const isRemove = element.getAttribute('name') === 'remove-icon';
        icon = element;
        return isRemove;
      });

      let themeIcon;
      const isThemeMenu = elements.some((element) => {
        const isTheme = element.classList.contains('dot-wrapper');
        themeIcon = element;
        return isTheme;
      });
      const targetElement = event.target.closest('.cell');
      const indexRow = targetElement.getAttribute('indexRow');
      const indexColumn = parseInt(targetElement.getAttribute('indexColumn'));

      const cell = this.journalNew.getDisplayCell(indexRow, indexColumn);
      const rowIndex = parseInt(targetElement.getAttribute('row-index'));
      const isHeader = targetElement.classList.contains('header');
      if (isRemoveIcon) {
        const targetElement = icon.closest('.cell');
        const indexRow = targetElement.getAttribute('indexRow');
        const indexColumn = targetElement.getAttribute('indexColumn');

        const cell = this.journalNew.getDisplayCell(indexRow, indexColumn);
        this.openConfirm(cell);

        return;
      }
      if (isThemeMenu) {
        const targetElement = themeIcon.closest('.cell');
        this.openThemeMenu(targetElement);
      }
      if (
        isHeader &&
        [addItemsEnum.CORRECTION, addItemsEnum.THEME_CORRECTION].includes(
          cell.type
        )
      ) {
        return;
      }
      if (isHeader && this.selectedType) {
        this.journalNew.startSelectHeaderByType(
          rowIndex,
          this.selectedType.type
        );
        return;
      } else if (isHeader && indexColumn === 1) {
        this.journalNew.sortColumn(indexColumn, indexRow);
      }

      if (cell.isEditable() && !cell.edit) {
        cell.toggleEdit();
        this.$nextTick(() => {
          const input = targetElement.querySelector('.edit-input');
          if (input) {
            input.focus();
            input.select();
          }
        });
        return;
      }

      const isLabelNumber = !isNaN(parseInt(cell.label, 10));
      if (
        isLabelNumber &&
        cell.id &&
        !isHeader &&
        !cell.edit &&
        [addItemsEnum.LESSON].includes(cell.type)
      ) {
        this.emit(JournalEventEnum.OPEN_LESSON, { value: cell.id });
      }
    },
    emit(type, data) {
      this.$emit('journalEvent', {
        type,
        data,
      });
    },
  },
};
</script>

<style lang="less">
.theme--light.v-input input,
.theme--light.v-input textarea {
  padding: 5px 0 10px 0;
  color: #f15a22;
}
.toolbar-filter {
  width: 200px;
  height: 78px;
  padding: 20px;

  .v-text-field {
    padding-top: 0;
    margin-top: 0;
  }

  .v-input__slot {
    height: 32px;
  }

  .v-select__selection--comma {
    display: none;
  }

  .v-select__selection--comma:first-child {
    display: block;
  }

  .v-select__selection--comma:not(:only-of-type) {
    &:after {
      content: '...';
    }
  }
}
</style>

<style lang="less" scoped>
.teacher-journal {
  margin-bottom: 50px;
  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  ::-webkit-scrollbar-track {
    -webkit-border-radius: 3px;
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb {
    -webkit-border-radius: 3px;
    border-radius: 3px;
    background: var(--theme-accent-decorative);
  }
}

.journal-controls {
  background: #f8faf9;
  border: 1px solid #e2e8e5;
  border-radius: 14px;
  margin-bottom: 18px;
  padding: 14px;
}

.journal-controls__primary {
  align-items: center;
  display: flex;
  gap: 14px;
  justify-content: space-between;
}

.journal-controls__filters {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 10px;
  min-width: 0;
}

.journal-controls__search {
  flex: 1 1 250px;
  max-width: 320px;
}

.journal-controls__filter {
  flex: 1 1 230px;
  max-width: 310px;
}

.journal-controls__search::v-deep .v-input__slot,
.journal-controls__filter::v-deep .v-input__slot,
.journal-lessons-navigation__page-size::v-deep .v-input__slot {
  background: #ffffff;
  border-radius: 10px;
  min-height: 42px !important;
}

.journal-controls__search::v-deep input,
.journal-controls__filter::v-deep input {
  color: #172033 !important;
  padding-bottom: 8px !important;
  padding-top: 8px !important;
}

.journal-controls__filter::v-deep .v-select__selections {
  align-content: flex-start;
  padding-bottom: 4px;
  padding-top: 10px;
}

.journal-controls__filter::v-deep .v-select__selections input {
  flex: 0 0 0;
  min-width: 0;
  padding: 0 !important;
  width: 0;
}

.journal-controls__filter::v-deep .v-chip {
  margin-bottom: 2px;
  margin-top: 2px;
}

.journal-controls__color {
  align-items: center;
  color: #465160;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  gap: 3px;
  white-space: nowrap;
}

.journal-controls__color button {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 6px 2px;
}

.journal-controls__color button:focus-visible {
  outline: 3px solid var(--theme-accent-decorative);
  outline-offset: 1px;
}

.journal-controls__color::v-deep .v-input--selection-controls__input {
  margin-right: 2px;
}

.journal-controls__actions {
  flex: 0 0 auto;
}

.journal-controls__add {
  border-color: var(--theme-header-background) !important;
  border-radius: 10px;
  color: var(--theme-header-background) !important;
  min-height: 42px;
}

.journal-controls__add::v-deep .v-btn__content,
.journal-controls__add::v-deep .v-icon {
  color: var(--theme-header-background) !important;
}

.journal-controls__add:hover {
  background: var(--theme-banner-background) !important;
  border-color: var(--theme-header-background) !important;
}

.journal-controls__add:focus-visible {
  outline: 3px solid var(--theme-accent-decorative);
  outline-offset: 2px;
}

.journal-add-column {
  align-items: center;
  background: #f2f6fd;
  border: 1px solid #d6e1f4;
  border-radius: 11px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-top: 12px;
  padding: 11px 12px;
}

.journal-add-column__description {
  align-items: flex-start;
  display: flex;
  gap: 9px;
  min-width: 0;
}

.journal-add-column__description strong,
.journal-add-column__description span {
  display: block;
}

.journal-add-column__description strong {
  color: #263448;
  font-size: 13px;
}

.journal-add-column__description span {
  color: #69778a;
  font-size: 12px;
  margin-top: 2px;
}

.journal-add-column__selection {
  color: #69778a;
  display: flex;
  flex: 0 0 auto;
  font-size: 13px;
  gap: 5px;
}

.journal-add-column__selection strong {
  color: #263448;
}

.journal-add-column__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.journal-add-column__actions::v-deep .v-btn {
  border-radius: 9px;
  margin: 0;
}

.journal-column-editor {
  background: #ffffff;
  border: 1px solid #dce5e1;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(31, 41, 55, 0.2);
  display: flex;
  flex-direction: column;
  max-height: ~'calc(100vh - 104px)';
  overflow: hidden;
  position: fixed;
  right: 24px;
  top: 84px;
  width: 360px;
  z-index: 20;
}

.journal-column-editor__header {
  align-items: center;
  border-bottom: 1px solid #e5e9ec;
  display: flex;
  justify-content: space-between;
  padding: 16px 18px;
}

.journal-column-editor__header span {
  color: #2d68bd;
  display: block;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.journal-column-editor__header h3 {
  color: #172033;
  font-size: 19px;
  margin: 3px 0 0;
}

.journal-column-editor__header button {
  align-items: center;
  background: #f3f5f6;
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  display: flex;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.journal-column-editor__body {
  overflow-y: auto;
  padding: 17px 18px;
}

.journal-column-editor__body section + section {
  border-top: 1px solid #e8ecee;
  margin-top: 18px;
  padding-top: 18px;
}

.journal-column-editor__step {
  color: #526174;
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 9px;
  text-transform: uppercase;
}

.journal-column-editor__types {
  display: grid;
  gap: 7px;
  grid-template-columns: repeat(3, 1fr);
}

.journal-column-editor__types button {
  background: #f7f9fa;
  border: 1px solid #dfe5e8;
  border-radius: 9px;
  color: #4a586b;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  min-height: 40px;
  padding: 7px 8px;
}

.journal-column-editor__types .journal-column-editor__type--active {
  background: var(--theme-banner-background);
  border-color: var(--theme-border-decorative);
  color: var(--theme-header-background);
  box-shadow: inset 0 0 0 1px var(--theme-border-decorative);
}

.journal-column-editor__body p {
  color: #68768a;
  font-size: 13px;
  line-height: 1.45;
  margin: 0 0 11px;
}

.journal-column-editor__selection {
  align-items: center;
  background: #f6f8f9;
  border: 1px dashed #cbd5da;
  border-radius: 10px;
  display: flex;
  gap: 10px;
  min-height: 58px;
  padding: 10px 11px;
}

.journal-column-editor__selection--ready {
  background: var(--theme-banner-background);
  border-color: var(--theme-border-decorative);
  border-style: solid;
}

.journal-column-editor__selection span,
.journal-column-editor__selection strong {
  display: block;
}

.journal-column-editor__selection span {
  color: #7a8797;
  font-size: 10px;
  text-transform: uppercase;
}

.journal-column-editor__selection strong {
  color: #263448;
  font-size: 13px;
  line-height: 1.3;
  margin-top: 2px;
}

.journal-column-editor__note {
  align-items: flex-start;
  background: #f1f5fc;
  border-radius: 9px;
  color: #526174;
  display: flex;
  font-size: 12px;
  gap: 8px;
  line-height: 1.4;
  margin-top: 13px;
  padding: 9px 10px;
}

.journal-column-editor__footer {
  border-top: 1px solid #e5e9ec;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 13px 18px;
}

.journal-column-editor__footer::v-deep .v-btn {
  border-radius: 9px;
  margin: 0;
}

.journal-lessons-navigation {
  align-items: center;
  border-top: 1px solid #e1e7e4;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-top: 13px;
  padding-top: 13px;
}

.journal-lessons-navigation__summary {
  min-width: 0;
}

.journal-lessons-navigation__label {
  color: #778496;
  display: block;
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.journal-lessons-navigation__summary strong {
  color: #172033;
  display: block;
  font-size: 15px;
  margin-top: 2px;
}

.journal-lessons-navigation__controls {
  align-items: center;
  color: #68758a;
  display: flex;
  font-size: 13px;
  gap: 8px;
}

.journal-lessons-navigation__page-size {
  flex: 0 0 auto;
  max-width: 96px;
  min-width: 96px;
  width: 96px;
}

.journal-lessons-navigation__page-size::v-deep .v-select__slot {
  min-width: 0;
}

.journal-lessons-navigation__page-size::v-deep .v-select__selections {
  flex-wrap: nowrap;
  min-width: 0;
  overflow: visible;
  white-space: nowrap;
}

.journal-lessons-navigation__page-size::v-deep .v-select__selection {
  flex: 0 0 auto;
  max-width: none;
  overflow: visible;
  text-overflow: clip;
  white-space: nowrap;
}

.journal-lessons-navigation__page-size::v-deep .v-input__append-inner {
  flex: 0 0 auto;
  margin-left: auto;
  padding-left: 4px;
}

.journal-lessons-navigation__arrow,
.journal-lessons-navigation__range-toggle {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dce3e0;
  color: #465160;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
}

.journal-lessons-navigation__arrow {
  border-radius: 9px;
  height: 40px;
  width: 40px;
}

.journal-lessons-navigation__arrow:disabled {
  cursor: default;
  opacity: 0.38;
}

.journal-lessons-navigation__range-toggle {
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
  gap: 6px;
  min-height: 40px;
  padding: 7px 11px;
}

.journal-lessons-navigation__range-toggle--active {
  background: var(--theme-banner-background);
  border-color: var(--theme-border-decorative);
  color: var(--theme-header-background);
}

.journal-range-picker {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e0e6e3;
  border-radius: 11px;
  color: #465160;
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 10px;
  margin-top: 10px;
  padding: 10px 12px;
}

.journal-range-picker > span {
  font-weight: 750;
}

.journal-range-picker label {
  align-items: center;
  display: flex;
  gap: 6px;
}

.journal-range-picker input {
  background: #ffffff;
  border: 1px solid #cfd8d4;
  border-radius: 8px;
  color: #172033;
  font: inherit;
  height: 38px;
  line-height: 36px;
  outline: none;
  padding: 0 8px;
  width: 72px;
}

.journal-range-picker input:focus {
  border-color: var(--theme-header-background);
  box-shadow: 0 0 0 3px var(--theme-accent-decorative);
}

.journal-range-picker::v-deep .v-btn {
  border-radius: 8px;
  margin: 0 0 0 auto;
}

@media screen and (max-width: 1050px) {
  .journal-controls__primary,
  .journal-lessons-navigation {
    align-items: stretch;
    flex-direction: column;
  }

  .journal-controls__actions {
    align-self: flex-end;
  }

  .journal-lessons-navigation__controls {
    flex-wrap: wrap;
  }
}

@media screen and (max-width: 700px) {
  .journal-controls {
    padding: 10px;
  }

  .journal-controls__filters {
    align-items: stretch;
    flex-direction: column;
  }

  .journal-controls__search,
  .journal-controls__filter {
    max-width: none;
    width: 100%;
  }

  .journal-controls__color {
    min-height: 40px;
  }

  .journal-controls__actions,
  .journal-controls__add {
    align-self: stretch;
    width: 100%;
  }

  .journal-add-column {
    align-items: stretch;
    flex-direction: column;
  }

  .journal-add-column__actions::v-deep .v-btn {
    flex: 1;
  }

  .journal-lessons-navigation__range-toggle {
    flex: 1 1 auto;
  }

  .journal-range-picker::v-deep .v-btn {
    margin-left: 0;
    width: 100%;
  }

  .journal-column-editor {
    border-radius: 16px 16px 0 0;
    bottom: 0;
    left: 0;
    max-height: 78vh;
    right: 0;
    top: auto;
    width: 100%;
  }
}

.controls-toolbar-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  .v-input--selection-controls {
    margin-top: 0;
    padding-top: 0;

    .v-input__control {
      .v-input__slot {
        margin-bottom: 0;
      }
    }
  }
  .select-item {
    display: flex;
    .theme-control-wrapper {
      display: flex;
      margin: 0 0 0 50px;
      width: 550px;
      height: 36px;

      .theme-type-wrapper {
        display: flex;
      }

      .theme-stat-val,
      .theme-end-val {
        pointer-events: none;
        width: 70px;
        margin: 0 8px 0 8px;

        .v-text-field {
          padding: 0;
          margin: 0;
        }
      }

      button {
        margin: 0 0 0 8px;
      }
    }
  }
}
.toolbar-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  .journal-label {
    color: #083c39;
    width: 71px;
    height: 25px;
    left: 295px;
    top: 561px;

    font-family: Open Sans;
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 25px;

    margin: 26px 0 26px 0;
  }
  .toolbar-left-side {
    display: flex;

    .toolbar-search {
      width: 240px;
      height: 78px;
      padding: 20px 0 0 0;
      .v-text-field {
        padding-top: 0;
        margin-top: 0;
      }
    }
    .toolbar-filter {
      width: 200px;
      height: 78px;
      padding: 20px;

      .v-text-field {
        padding-top: 0;
        margin-top: 0;
      }

      .v-input__slot {
        height: 32px;
      }

      .v-select__selection--comma {
        display: none;
      }

      .v-select__selection--comma:first-child {
        display: block;
      }

      .v-select__selection--comma:not(:only-of-type) {
        &:after {
          content: '...';
        }
      }
    }
  }
  .toolbar-right-side {
    display: flex;
  }

  .toolbar-spacer {
    width: 240px;
    height: 78px;
    padding: 20px;
  }

  .toolbar-star-pos {
    display: flex;
    width: 145px;
    height: 78px;
    padding: 20px 0 20px 0;

    .toolbar-star-pos-label {
      font-family: Open Sans;
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 22px;
      white-space: nowrap;

      width: 89px;
      margin: 5px 0 5px 0;
    }

    .toolbar-star-pos-number {
      width: 44px;

      .v-text-field {
        padding-top: 0;
        margin-top: 0;
      }
    }

    .start-input {
      width: 30px;
    }
  }

  .toolbar-pagination {
    display: flex;
    justify-content: flex-end;
    width: 350px;
    height: 78px;
    padding: 20px 0 20px 0;
    font-family: Open Sans;
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 22px;

    .toolbar-pagination-pages-label {
      width: 107px;
      height: 22px;
      margin: 5px 0 5px 0;
    }

    .pagination-select {
      width: 55px;
      height: 36px;
      margin: 0 0 0 6px;
      line-height: 18px;

      .v-text-field {
        padding-top: 0;
        margin-top: 0;
      }
    }

    .toolbar-pagination-range-label {
      width: auto;
      height: 22px;
      margin: 5px;
    }
    .navigation-wrapper {
      width: 65px;
      display: flex;
      justify-content: flex-end;

      .toolbar-pagination-pages-nav {
        &.disabled {
          pointer-events: none;
          .v-application .black--text {
            color: red;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 600px) {
  .toolbar-wrapper {
    display: unset;
  }
}

.tbl {
  background: #ffffff;
  border: 1px solid #dfe5e8;
  border-radius: 13px;
  box-shadow: 0 6px 20px rgba(31, 41, 55, 0.07);
  max-height: ~'min(68vh, 720px)';
  overflow: auto;
  overscroll-behavior: contain;
  isolation: isolate;
  position: relative;
  scrollbar-color: var(--theme-accent-decorative) var(--theme-surface-soft);
  scrollbar-width: thin;
  width: 100%;
  z-index: 0;
}

.tbl::-webkit-scrollbar {
  height: 9px;
  width: 9px;
}

.tbl::-webkit-scrollbar-track {
  background: var(--theme-surface-soft);
}

.tbl::-webkit-scrollbar-thumb {
  background: var(--theme-accent-decorative);
  border: 2px solid var(--theme-surface-soft);
  border-radius: 999px;
}

.tbl::-webkit-scrollbar-thumb:hover {
  background: var(--theme-header-background);
}

.row {
  box-sizing: border-box;
  display: flex;
  width: 4000px;
  height: 64px;
  margin: 0;
  min-width: 100%;
}

.row:first-child {
  background: #f5f7f8;
  height: 62px;
  position: sticky;
  top: 0;
  z-index: 5;
}

.row + .row {
  border-top: 1px solid #e8ecef;
}

.cell {
  align-items: center;
  background: #ffffff;
  border-right: 1px solid #edf0f2;
  color: #253348;
  display: flex;
  flex: 0 0 74px;
  height: 58px;
  justify-content: center;
  text-align: center;
  width: 74px;
  transition: background 0.12s ease;

  .label-wrapper {
    align-items: center;
    border-radius: 8px;
    display: flex;
    font-size: 14px;
    font-weight: 750;
    height: 36px;
    justify-content: center;
    min-width: 36px;
    padding: 0 7px;
    width: auto;
    &.red-val {
      background: var(--theme-surface-decorative);
      color: var(--theme-header-background);
    }
    &.yellow-val {
      background: var(--theme-banner-background);
      color: var(--theme-header-background);
    }
    &.blue-val {
      background: var(--theme-surface-decorative);
      color: var(--theme-header-background);
    }
    &.green-val {
      background: var(--theme-banner-background);
      color: var(--theme-header-background);
    }
  }
  .right-margin {
    margin-right: 30px;
  }

  .cell-label {
    align-items: center;
    justify-content: center;
    display: flex;
    max-width: 100%;
    min-width: 0;

    .lesson-remove-ico {
      padding: 9px 0 0 0;
    }
  }
  .header-cell-label {
    align-items: center;
    display: flex;
    font-size: 13px;
    font-weight: 750;
    justify-content: center;
    line-height: 1.25;
    max-width: 100%;
    overflow: hidden;
    padding: 0 7px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .theme-wrapper {
    padding-top: 3px;
  }

  .v-input__slot {
    margin-bottom: 0;
  }

  .edit-filde {
    // margin-top: 10px;
    &.v-input--dense > .v-input__control > .v-input__slot {
      margin-bottom: -10px;
    }
  }
}

.cell:not(.sticky) {
  flex-grow: 1;
}

.custom-size {
  flex-basis: 152px;
  justify-content: center;
  width: 152px;
}

.sticky {
  position: sticky;
  text-align: left;
}

.number-lesson {
  flex-basis: 78px;
  left: 0;
  width: 78px;
  z-index: 3;
  .cell-label {
    height: auto;
    padding: 0;
    width: auto;
  }
  &.odd {
    background-color: #f7f9fa;
  }
  &.even {
    background-color: white;
  }
}

.student-name {
  flex-basis: 234px;
  justify-content: flex-start;
  left: 78px;
  padding: 0 16px;
  width: 234px;
  z-index: 3;
  .cell-label {
    justify-content: flex-start;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &.odd {
    background-color: #f7f9fa;
  }
  &.even {
    background-color: white;
  }
}

.cell.header {
  background: #f5f7f8;
  border-bottom: 1px solid #dce2e5;
  color: #172033;
  height: 62px;
  position: sticky;
  top: 0;
  z-index: 4;
}

.cell.header.number-lesson {
  z-index: 6;
}

.cell.header.student-name {
  box-shadow: 8px 0 12px -12px rgba(31, 41, 55, 0.55);
  z-index: 6;
}

.student-name:not(.header) {
  box-shadow: 8px 0 12px -12px rgba(31, 41, 55, 0.45);
  font-size: 14px;
  font-weight: 650;
}

.row:not(:first-child):hover .cell {
  background: var(--theme-surface-decorative);
}

.row:not(:first-child):hover .number-lesson,
.row:not(:first-child):hover .student-name {
  background: var(--theme-banner-background);
}

.grade-cell {
  font-size: 14px;
  font-weight: 700;
}

.grade-cell--empty {
  color: #a3acb8;
  font-weight: 500;
}

.rotate-180 {
  transform: rotate(180deg);
}

.active-page {
  background-color: var(--theme-header-background);
}
.in-range {
  background-color: var(--theme-banner-background) !important;
  box-shadow: inset 0 3px var(--theme-header-background),
    inset 0 -3px var(--theme-header-background);
}

.tooltip-hide {
  opacity: 0;
}
.tooltip-show {
  opacity: 1;
}

.edit-input {
  background: var(--theme-banner-background);
  border: 1px solid var(--theme-banner-border);
  border-radius: 8px;
  color: var(--theme-banner-text);
  font-size: 13px;
  height: 36px;
  margin: 0 auto;
  outline: none;
  padding: 0 7px;
  text-align: center;
  width: 88px;
  &.set-mark {
    font-size: 14px;
    font-weight: 750;
    height: 36px;
    width: 48px;
  }

  &.invalid {
    background: #fff7f7;
    border-color: #d85b55;
    color: #a63732;
  }
}
.edit-input:focus {
  border-color: var(--theme-header-background);
  box-shadow: 0 0 0 3px var(--theme-accent-decorative);
  &.invalid {
    border-color: #d85b55;
    box-shadow: 0 0 0 3px rgba(216, 91, 85, 0.16);
  }
}
.edit-input:disabled {
  background: var(--theme-surface-decorative);
  color: var(--theme-banner-text);
  cursor: not-allowed;
  opacity: 0.72;
}

.info-tooltip-hide {
  opacity: 0;
}

.info-tooltip-show {
  background-color: #81d8be;
  color: black;
  opacity: 1;
}

.progress-bar {
  width: 100%;
  z-index: 2;
  .progress-line {
    height: 5px;
    width: 0;
    background: var(--theme-header-background);
    position: relative;
    z-index: 2;
  }
}

.up {
  transform: rotate(180deg);
}
.dot-wrapper {
  margin-left: 18px;
}
.dots-menu {
  width: 10px;
  height: 10px;
  background-image: radial-gradient(circle, #616161 3px, transparent 3px);
}

.menu-item {
  cursor: pointer;
}

.nowrap-label {
  white-space: nowrap;
}

.page-number {
  width: 60px;
}

.progress-cicular {
  position: absolute;
  z-index: 10;
  left: 50%;
  padding-top: 100px;
}

.in-progress {
  opacity: 0.5;
  pointer-events: none;
}

.teacher-journal--student {
  .toolbar-wrapper {
    display: none;
  }

  // The student journal contains a compact grades summary, so its header does
  // not need to follow the page scroll and overlap the fixed student app bar.
  .row:first-child,
  .cell.header {
    position: static;
  }

  .journal-filter-row {
    margin: 0 0 16px !important;
    padding: 0 !important;
  }

  .journal-filter-column {
    margin: 0 !important;
    padding: 0 !important;
  }

  .journal-filter-controls {
    align-items: center;
    background: var(--theme-surface-soft);
    border: 1px solid var(--theme-border-decorative);
    border-radius: 12px;
    gap: 14px;
    justify-content: flex-start !important;
    min-height: 58px;
    padding: 8px 10px;
    max-width: 460px;
    width: 100%;
  }

  .journal-color-control {
    align-items: center;
    color: var(--theme-banner-text);
    cursor: pointer;
    display: flex;
    flex: 0 0 auto;
    font-size: 13px;
    font-weight: 700;
    gap: 5px;
  }

  .journal-color-switch {
    flex: 0 0 auto;
  }

  .journal-color-switch::v-deep .v-input--selection-controls__input {
    margin-right: 2px;
  }

  .journal-color-switch.v-input--is-label-active::v-deep
    .v-input--switch__track,
  .journal-color-switch.v-input--is-label-active::v-deep
    .v-input--switch__thumb {
    color: var(--theme-header-background) !important;
  }

  .journal-color-switch.v-input--is-label-active::v-deep
    .v-input--switch__track {
    opacity: 0.38;
  }

  .journal-color-switch.v-input--is-label-active::v-deep
    .v-input--switch__thumb {
    opacity: 1;
  }

  .journal-filter-select {
    flex: 1 1 auto;
    max-width: 250px;
  }

  .journal-filter-select::v-deep .v-input__slot {
    background: var(--theme-app-background);
    min-height: 40px;
  }

  .journal-filter-select::v-deep .v-label {
    color: var(--theme-banner-text);
    font-size: 13px;
  }

  .journal-filter-select.v-input--is-focused:not(.error--text)::v-deep
    .v-label,
  .journal-filter-select.v-input--is-focused:not(.error--text)::v-deep
    .v-icon,
  .journal-filter-select::v-deep .v-select__selection,
  .journal-filter-select::v-deep .v-input__append-inner .v-icon,
  .journal-filter-select::v-deep .v-chip,
  .journal-filter-select::v-deep .v-chip .v-icon {
    color: var(--theme-header-background) !important;
  }

  .journal-filter-select.v-input--is-focused:not(.error--text)::v-deep
    fieldset {
    border-color: var(--theme-header-background) !important;
  }

  .journal-filter-select::v-deep .v-chip {
    background: var(--theme-banner-background) !important;
  }

  .journal-filter-select::v-deep .v-text-field__details {
    display: none;
  }

  @media screen and (max-width: 600px) {
    .journal-filter-controls {
      align-items: stretch;
      flex-direction: column;
      gap: 8px;
      width: 100%;
    }

    .journal-filter-select {
      max-width: none;
      width: 100%;
    }
  }
}
</style>
